import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { QUERY_KEYS } from '@/lib/constants';
import { getSubscriptionDetails } from '@/lib/api/settings/api';
import type { SubscriptionDetails } from '@/lib/api/settings/types';
import { useAuthStore } from '@/stores';

/**
 * Hook to fetch subscription details.
 *
 * NOTE: This uses useQuery directly instead of useStanQuery because:
 * - The API returns 400 when user has no active subscription (expected behavior)
 * - Vue handles this silently by catching the error and falling back to user data
 * - We don't want to show an error toast for this expected case
 */
export const useSubscriptionDetails = () => {
    const isAuthenticated = useAuthStore(state => state.isAuthenticated);

    const { data, isLoading, error, refetch } = useQuery<SubscriptionDetails, Error>({
        queryKey: [QUERY_KEYS.SUBSCRIPTION_DETAILS],
        queryFn: getSubscriptionDetails,
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
        enabled: isAuthenticated,
        // Don't retry on 400 errors (no active subscription is expected for some users)
        retry: (failureCount, error) => {
            if (isAxiosError(error) && error.response?.status === 400) return false;
            return failureCount < 3;
        },
    });

    return { data, isLoading, error, refetch };
};
